import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, MessageSquareReply, AlertTriangle, ClipboardCheck, LayoutDashboard, Columns3, CheckCircle2, TimerReset, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import TicketModal from './TicketModal';
import LogRequestDialog from './LogRequestDialog';
import { useAuth } from '../../contexts/AuthContext';

const PRIORITY_DOT = { critical: '#dc2626', high: '#f59e0b', medium: '#3b82f6', low: '#94a3b8' };

function ageDays(createdAt) {
    if (!createdAt) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24)));
}

function BallTag({ ballWith }) {
    return (
        <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${ballWith === 'client' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
            {ballWith === 'client' ? 'Client' : 'Us'}
        </span>
    );
}

function TicketRow({ t, onOpen, sub }) {
    return (
        <div onClick={() => onOpen(t.id)} data-testid={`inbox-row-${t.id}`}
             className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--bm-border)] bg-white hover:border-[color:var(--bm-primary)] cursor-pointer transition">
            <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: PRIORITY_DOT[t.priority] || '#94a3b8' }} />
                <div className="min-w-0">
                    <div className="text-[13px] font-medium text-[color:var(--bm-text)] truncate">{t.title}</div>
                    <div className="text-[11px] text-[color:var(--bm-text-subtle)] truncate">{t.ticket_number} · {t.platform} · {t.client_name || '—'}</div>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {sub && <span className="text-[11px] text-[color:var(--bm-text-subtle)]">{sub}</span>}
                {t.origin === 'manual' && <span className="text-[9.5px] font-semibold rounded-full px-1.5 py-0.5 bg-violet-100 text-violet-700" data-testid={`inbox-row-manual-${t.id}`}>MANUAL</span>}
                <BallTag ballWith={t.ball_with} />
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, items, emptyText, renderSub, testId, onOpen }) {
    return (
        <div className="bg-[color:var(--bm-bg-mute)] rounded-xl p-4" data-testid={testId}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={15} className="text-[color:var(--bm-primary)]" />
                <div className="text-[13px] font-semibold text-[color:var(--bm-text)]">{title}</div>
                <div className="text-[11px] bg-white rounded-full px-2 py-0.5 text-[color:var(--bm-text-subtle)]">{items.length}</div>
            </div>
            <div className="space-y-1.5">
                {items.length === 0 && <div className="text-[12px] text-[color:var(--bm-text-subtle)] px-1 py-2">{emptyText}</div>}
                {items.map(t => <TicketRow key={t.id} t={t} onOpen={onOpen} sub={renderSub ? renderSub(t) : null} />)}
            </div>
        </div>
    );
}

export default function SupportInbox() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const canEdit = can('support', 'edit');
    const canDelete = can('support', 'delete');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openTicketId, setOpenTicketId] = useState(null);
    const [logRequestOpen, setLogRequestOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/support/inbox');
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const totalAttention = data ? Object.values(data.counts || {}).reduce((a, b) => a + b, 0) : 0;

    return (
        <div data-testid="support-inbox-page" className="bm-fade-in">
            <PageHeader
                title="Needs My Attention"
                description="Everything across the Support Hub that needs you right now — new requests, client replies, overdue items, and improvements awaiting your review."
                actions={
                    <div className="flex items-center gap-2">
                        {canEdit && (
                            <Button variant="outline" onClick={() => setLogRequestOpen(true)} className="h-9" data-testid="support-log-request-btn">
                                <PhoneCall size={15} className="mr-1.5" /> Log a request
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => navigate('/support-hub/board')} className="h-9" data-testid="support-board-link">
                            <Columns3 size={15} className="mr-1.5" /> Board
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/support-hub/dashboard')} className="h-9" data-testid="support-dashboard-link">
                            <LayoutDashboard size={15} className="mr-1.5" /> Dashboard
                        </Button>
                    </div>
                }
            />
            <div className="px-4 sm:px-8 py-4 sm:py-6">
                {loading ? (
                    <div className="text-[color:var(--bm-text-muted)]">Loading…</div>
                ) : totalAttention === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="support-inbox-empty">
                        <CheckCircle2 size={36} className="text-emerald-400" />
                        <div className="mt-3 text-[14px] font-semibold text-[color:var(--bm-text)]">You're all caught up</div>
                        <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1">Nothing needs your attention right now.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Section icon={Inbox} title="New — needs triage" items={data.triage} emptyText="No new requests waiting to be triaged."
                                 renderSub={t => `${ageDays(t.created_at)}d old`} testId="inbox-section-triage" onOpen={setOpenTicketId} />
                        <Section icon={MessageSquareReply} title="Client replied" items={data.client_replied} emptyText="No unanswered client replies."
                                 testId="inbox-section-replied" onOpen={setOpenTicketId} />
                        <Section icon={AlertTriangle} title="Overdue" items={data.overdue} emptyText="Nothing is overdue."
                                 renderSub={t => t.target_completion_date} testId="inbox-section-overdue" onOpen={setOpenTicketId} />
                        <Section icon={ClipboardCheck} title="Improvements awaiting your review" items={data.review_improvements} emptyText="No improvements waiting on a commercial review."
                                 renderSub={t => `${ageDays(t.created_at)}d old`} testId="inbox-section-review" onOpen={setOpenTicketId} />
                        <Section icon={TimerReset} title="First response overdue" items={data.response_overdue} emptyText="No first-response SLAs have been missed."
                                 renderSub={t => `${ageDays(t.created_at)}d old`} testId="inbox-section-response-overdue" onOpen={setOpenTicketId} />
                    </div>
                )}
            </div>
            <TicketModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} onChanged={load} canEdit={canEdit} canDelete={canDelete} />
            <LogRequestDialog open={logRequestOpen} onClose={() => setLogRequestOpen(false)} onCreated={(id) => { load(); setOpenTicketId(id); }} />
        </div>
    );
}
